import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ArrowUpRight, Shirt, Footprints, GraduationCap, Wind, ArrowDown, HardHat, Layers, Heart, Trophy, Gem, ShoppingBag, Wallet, Sparkles, Package } from 'lucide-react';
import { categories } from '@/mocks/data';

const iconMap: Record<string, React.ElementType> = {
  'Shoes': Footprints,
  'Hoodies / Sweaters': GraduationCap,
  'T-Shirts': Shirt,
  'Jackets': Wind,
  'Pants / Shorts': ArrowDown,
  'Headwear': HardHat,
  'Sets': Layers,
  'Underwear / Underpants': Heart,
  'Jersey': Trophy,
  'Accessories': Gem,
  'Bags': ShoppingBag,
  'Belts': Wallet,
  'Perfumes': Sparkles,
  'Other Stuff': Package,
};

export default function Categories() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');

  const handleSelect = (category: (typeof categories)[0]) => {
    setSelectedCategory(category.name);
    setDropdownOpen(false);
    window.open(category.url, '_blank', 'noopener,nofollow');
  };

  return (
    <section id="categories" className="relative w-full py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-6 lg:px-8 xl:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="text-sm font-semibold text-primary-600 tracking-wider uppercase mb-3 block">
              Browse
            </span>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              {t('categories.title')}
            </h2>
            <p className="text-foreground-600 text-base md:text-lg max-w-2xl mx-auto">
              {t('categories.description')}
            </p>
          </div>

          <div className="relative max-w-md mx-auto mb-12 md:mb-16">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-4 bg-background-50 border border-background-300/60 rounded-xl text-foreground-800 font-medium hover:border-primary-400 transition-colors shadow-sm"
            >
              <span>{selectedCategory || t('categories.select')}</span>
              <ChevronDown className={`w-5 h-5 text-foreground-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 rounded-xl border border-background-300/60 shadow-xl overflow-hidden z-20 animate-fade-in">
                {categories.map((category) => (
                  <button
                    key={category.name}
                    onClick={() => handleSelect(category)}
                    className="w-full flex items-center justify-between px-5 py-3.5 text-left text-foreground-700 hover:bg-primary-50/60 transition-colors border-b border-background-200/40 last:border-0"
                  >
                    <span className="font-medium text-sm">{category.name}</span>
                    <ArrowUpRight className="w-4 h-4 text-foreground-400" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-3 md:gap-4">
            {categories.map((category, index) => {
              const Icon = iconMap[category.name] || Package;
              return (
                <a
                  key={category.name}
                  href={category.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group relative flex flex-col items-center text-center p-4 md:p-5 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-300/50 hover:bg-primary-50/30 transition-all duration-300 animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden mb-3">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-50 flex items-center justify-center mb-2 group-hover:bg-primary-100 transition-colors">
                    <Icon className="w-4 h-4 md:w-5 md:h-5 text-primary-600" />
                  </div>
                  <span className="text-xs md:text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors leading-tight">
                    {category.name}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}